import { useState, useEffect } from 'react';
import AuthWrapper from './components/AuthWrapper';
import AuthDebugPanel from './components/AuthDebugPanel';
import { useAuth, supabase } from './auth';

// API Configuration - MUST use environment variable for production
const API_URL = process.env.REACT_APP_API_URL;

function App() {
  // Secure authentication - fixed ESLint unused variable
  const { loading: authLoading, isAuthenticated, userId } = useAuth();

  const [resume, setResume] = useState(null);
  const [matches, setMatches] = useState([]);
  const [aiTitles, setAiTitles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Always use dark mode
  const darkMode = true;
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [activeTab, setActiveTab] = useState('matches');
  const [location, setLocation] = useState('Remote'); // Default to Remote
  const [country, setCountry] = useState('US'); // Default to US
  const [showATSTips, setShowATSTips] = useState({});
  const [companyCount, setCompanyCount] = useState('7,500');
  const [atsCount, setAtsCount] = useState(16);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [upgradeData, setUpgradeData] = useState(null);

  // Filter + sort state for results page
  const [filterRemote, setFilterRemote] = useState(false);
  const [filterType, setFilterType] = useState('all');   // 'all' | 'full-time' | 'part-time' | 'contract' | 'internship'
  const [filterSalary, setFilterSalary] = useState(false);
  const [filterATS, setFilterATS] = useState('all');
  const [sortBy, setSortBy] = useState('score');         // 'score' | 'recent'

  // Collect distinct ATS values present in results for the ATS filter dropdown
  const atsOptions = [...new Set(
    matches
      .map(m => m.ats_type)
      .filter(v => v && v !== 'unknown' && v !== 'daily_scraper')
  )].sort();

  // Collect distinct employment-type values (normalised to lowercase)
  const typeOptions = [...new Set(
    matches
      .map(m => (m.employment_type || '').toLowerCase().trim())
      .filter(Boolean)
  )].sort();

  // Apply filters then sort — this is what actually renders
  const filteredMatches = matches
    .filter(m => {
      if (filterRemote && !m.remote_ok) return false;
      if (filterSalary && !m.salary_range) return false;
      if (filterType !== 'all') {
        const t = (m.employment_type || '').toLowerCase();
        if (!t.includes(filterType)) return false;
      }
      if (filterATS !== 'all') {
        if ((m.ats_type || '') !== filterATS) return false;
      }
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'recent') {
        // null days_ago goes to the bottom
        if (a.days_ago == null && b.days_ago == null) return 0;
        if (a.days_ago == null) return 1;
        if (b.days_ago == null) return -1;
        return a.days_ago - b.days_ago;
      }
      // default: score descending
      return (b.score || 0) - (a.score || 0);
    });

  const filtersActive = filterRemote || filterSalary || filterType !== 'all' || filterATS !== 'all';

  const resetFilters = () => {
    setFilterRemote(false);
    setFilterSalary(false);
    setFilterType('all');
    setFilterATS('all');
  };

  useEffect(() => {
    // Always enable dark mode
    document.documentElement.classList.add('dark');

    // Fetch dynamic company and ATS counts
    const fetchCompanyData = async () => {
      try {
        const response = await fetch(`${API_URL}/company-count`);
        const data = await response.json();
        // Handle new Google Search API response format
        if (data.ats_systems_supported) {
          setAtsCount(data.ats_systems_supported);
          const total = data.total || '7,500';
          setCompanyCount(typeof total === 'string' ? total.replace('+', '') : `${total}`);
        } else {
          const total = data.total || '7,500';
          setCompanyCount(typeof total === 'string' ? total.replace('+', '') : `${total}`);
          setAtsCount(Object.keys(data.by_platform || {}).length || 16);
        }
      } catch (error) {
        console.log('Using default counts:', error);
      }
    };

    fetchCompanyData();
  }, []); // Run once on mount

  // Proactive onboarding check when user signs in
  useEffect(() => {
    const checkUserLimits = async () => {
      if (!isAuthenticated || !userId) return;

      try {
        // Import the secure API call helper
        const { secureApiCall } = await import('./auth/secureSupabase');

        // Use the secure API call helper which handles JWT authentication
        const data = await secureApiCall('/search-limits', {
          method: 'GET'
        });

        const { usage } = data;

        // Show upgrade prompt if user is at their limit
        if (usage.remaining === 0) {
          console.log('🚨 User at search limit - showing proactive upgrade prompt');
          showUpgradePrompt({
            usage: usage,
            subscription_tier: data.subscription?.tier || 'free',
            upgrade_message: 'Upgrade to continue searching for jobs'
          });
        }
      } catch (error) {
        console.log('Could not check user limits:', error);
        // Don't show error to user - this is a background check
      }
    };

    // Only check after auth is complete and we have userId
    if (isAuthenticated && userId && !authLoading) {
      checkUserLimits();
    }
  }, [isAuthenticated, userId, authLoading]);

  const showUpgradePrompt = (errorData) => {
    setUpgradeData(errorData);
    setShowUpgrade(true);
    setLoading(false);
    setProgress(0);
    setCurrentStep('');
  };

  const redirectToUpgrade = async () => {
    try {
      // Add user context and tracking to the redirect URL
      const params = new URLSearchParams({
        source: 'app',
        userId: userId || '',
        reason: 'search_limit_reached',
        timestamp: Date.now().toString()
      });

      // Show brief loading state while redirecting
      setLoading(true);
      setCurrentStep('Redirecting to upgrade page...');

      // Graceful redirect with context preservation to dedicated pricing page
      const upgradeUrl = `https://atsinsiderai.com/pricing?${params.toString()}`;

      console.log('🔄 Redirecting to pricing page with context:', upgradeUrl);

      // Small delay for better UX (users can see the loading state)
      setTimeout(() => {
        window.location.href = upgradeUrl;
      }, 500);

    } catch (error) {
      console.error('Error during upgrade redirect:', error);
      // Fallback to dedicated pricing page
      window.location.href = 'https://atsinsiderai.com/pricing';
    }
  };

  const handleUpload = async () => {
    // Force clear everything first
    setError('');
    setMatches([]);
    setAiTitles([]);
    setShowResults(false);
    resetFilters();
    setSortBy('score');

    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    if (!resume || !allowedTypes.includes(resume.type)) {
      setError('Please select a valid PDF or Word document (.pdf, .doc, .docx)');
      return;
    }

    console.log('🚀 UPLOAD STARTED - Connecting to REAL BACKEND');
    setLoading(true);
    setProgress(10);
    setCurrentStep('Connecting to backend server...');

    // ✅ SECURITY FIX: Use authenticated user ID instead of random UUID
    if (!isAuthenticated || !userId) {
      setError('Authentication required. Please sign in to continue.');
      setLoading(false);
      return;
    }

    const formData = new FormData();
    formData.append("file", resume);
    formData.append("user_id", userId); // Use persistent authenticated user ID
    formData.append("location", location);
    formData.append("country", country);
    formData.append("tier", "unlimited"); // Use unlimited for testing

    const backendUrl = `${API_URL}/search-jobs`;
    console.log('📡 Making request to:', backendUrl, '(NEW ENDPOINT WITH PAYWALL)');

    try {
      setCurrentStep('Uploading file to server...');
      setProgress(30);

      const response = await fetch(backendUrl, {
        method: "POST",
        body: formData,
        // Explicitly set headers to avoid caching
        headers: {
          'Cache-Control': 'no-cache',
        },
      });
      
      console.log('📨 Response received:', response);
      console.log('📊 Response status:', response.status);
      console.log('📋 Response headers:', [...response.headers.entries()]);
      
      if (!response.ok) {
        // Handle paywall (429) specially
        if (response.status === 429) {
          const errorData = await response.json().catch(() => ({}));
          showUpgradePrompt(errorData);
          return;
        }
        const errorData = await response.json().catch(() => ({}));
        const errorMsg = errorData.error || `Backend error: ${response.status} ${response.statusText}`;
        throw new Error(errorMsg);
      }
      
      setProgress(70);
      setCurrentStep('Searching 100+ companies for matching roles (this may take 2-3 minutes)...');
      
      const data = await response.json();
      console.log('🎯 BACKEND DATA RECEIVED:', data);
      
      if (data.error) {
        setError(`Backend error: ${data.error}`);
        return;
      }

      // Process REAL backend data - handle new Google Search API structure
      const realMatches = (data.jobs || []).map((job, index) => {
        console.log(`Processing job ${index + 1}:`, job);
        return {
          title: job.title || `Job ${index + 1}`,
          company: job.company || 'Unknown Company',
          score: job.relevance_score ? Math.round(job.relevance_score * 100) : (job.score || job.match_score || 50), // Convert 0-1 relevance_score to percentage
          description: job.description || job.snippet || 'Click to view full job details and requirements.',
          link: job.link || job.url || '#',
          ats: job.ats_system || job.ats || 'Unknown ATS',
          ats_info: job.ats_info || null,
          source_platform: job.source_platform || 'google_search',
          explanation: job.explanation || job.snippet,
          // Location and date fields
          location: job.location || null,
          date_posted: job.date_posted || null,
          days_ago_text: job.days_ago_text || null,
          // Additional metadata
          employment_type: job.employment_type || null,
          salary_range: job.salary_range || null,
          remote_ok: job.remote_ok || false,
          ats_type: job.ats_type || null
        };
      });

      const realAiTitles = (data.analysis?.job_titles || []).slice(0, 3).map((title, index) => {
        console.log(`Processing AI title ${index + 1}:`, title);
        
        // Get actual confidence score from AI analysis, NO fallbacks
        const confidenceScore = data.analysis?.title_confidence?.[index];
        
        // Get job-specific keywords for this title
        const jobTitle = typeof title === 'string' ? title : title.title || `AI Title ${index + 1}`;
        const jobSpecificKeywords = data.analysis?.job_specific_keywords?.[jobTitle] || [];
        const fallbackKeywords = data.analysis?.keywords || [];
        const finalKeywords = jobSpecificKeywords.length > 0 ? jobSpecificKeywords : fallbackKeywords;

        return {
          title: jobTitle,
          description: finalKeywords.length > 0 ? `Skills: ${finalKeywords.slice(0, 6).join(', ')}` : 'AI-generated suggestion',
          confidence: confidenceScore ? Math.max(75, Math.min(100, confidenceScore)) : null, // No fake scores
          keywords: finalKeywords
        };
      });

      console.log('✅ REAL MATCHES PROCESSED:', realMatches.length);
      console.log('✅ REAL AI TITLES PROCESSED:', realAiTitles.length);

      setMatches(realMatches);
      setAiTitles(realAiTitles);
      setProgress(100);
      setCurrentStep('Backend analysis complete!');
      
      setTimeout(() => setShowResults(true), 500);

    } catch (error) {
      console.error('❌ BACKEND CONNECTION FAILED:', error);
      setError(`Backend connection failed: ${error.message}. Check that your Flask server is running on ${API_URL}`);
    } finally {
      setTimeout(() => setLoading(false), 1000);
    }
  };

  const handleFileChange = (e) => {
    setResume(e.target.files[0]);
    setError('');
    setShowResults(false);
  };

  const resetApp = () => {
    setResume(null);
    setMatches([]);
    setAiTitles([]);
    setShowResults(false);
    setError('');
  };

  const handleTitleFeedback = async (titleText, feedbackType, titleIndex) => {
    try {
      console.log(`📝 Submitting feedback for "${titleText}": ${feedbackType}`);

      // Get user from auth
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        console.error('❌ No authenticated user');
        return;
      }

      const API_URL = 'https://jobsearchtool-production.up.railway.app';

      const response = await fetch(`${API_URL}/title-feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user.id,
          suggested_title: titleText,
          feedback: feedbackType,
          search_context: {
            resume_analysis: {}, // Could include full AI analysis here
            confidence_score: aiTitles[titleIndex]?.confidence || 0
          }
        })
      });

      const data = await response.json();

      if (data.success) {
        console.log('✅ Feedback recorded:', data.feedback_id);

        // Update UI to show feedback was recorded
        const updatedTitles = [...aiTitles];
        updatedTitles[titleIndex] = {
          ...updatedTitles[titleIndex],
          feedback: feedbackType
        };
        setAiTitles(updatedTitles);
      } else {
        console.error('❌ Failed to record feedback:', data.error);
      }
    } catch (error) {
      console.error('❌ Error submitting feedback:', error);
    }
  };

  const getScoreBadgeColor = (score) => {
    if (score >= 80) return 'bg-emerald-500';
    if (score >= 60) return 'bg-yellow-500';
    return 'bg-orange-500';
  };

  const getATSBadgeColor = (atsName) => {
    const colors = {
      'lever': 'bg-blue-500',
      'greenhouse': 'bg-green-500', 
      'ashby': 'bg-purple-500',
      'workday': 'bg-orange-500',
      'smartrecruiters': 'bg-indigo-500',
      'workable': 'bg-pink-500'
    };
    return colors[atsName?.toLowerCase()] || 'bg-gray-500';
  };

  const toggleATSTips = (jobIndex) => {
    setShowATSTips(prev => ({
      ...prev,
      [jobIndex]: !prev[jobIndex]
    }));
  };

  const renderATSTips = (atsInfo) => {
    if (!atsInfo) return null;
    
    return (
      <div className="mt-4 p-4 rounded-lg border bg-gray-800/50 border-gray-700">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-lg">⚙️</span>
          <h4 className="font-semibold text-white">
            ATS: {atsInfo.display_name}
          </h4>
        </div>
        
        {/* Strengths Section */}
        {atsInfo.strengths && atsInfo.strengths.length > 0 && (
          <div className="mb-3">
            <h5 className="text-sm font-medium text-green-500 dark:text-green-400 mb-1">✅ Strengths:</h5>
            <ul className="text-xs space-y-1">
              {atsInfo.strengths.map((strength, idx) => (
                <li key={idx} className="text-gray-300">{strength}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Weaknesses Section */}
        {atsInfo.weaknesses && atsInfo.weaknesses.length > 0 && (
          <div className="mb-3">
            <h5 className="text-sm font-medium text-red-500 dark:text-red-400 mb-1">❌ Weaknesses:</h5>
            <ul className="text-xs space-y-1">
              {atsInfo.weaknesses.map((weakness, idx) => (
                <li key={idx} className="text-gray-300">{weakness}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Optimization Tips Section */}
        {atsInfo.optimization_tips && atsInfo.optimization_tips.length > 0 && (
          <div>
            <h5 className="text-sm font-medium text-blue-600 dark:text-blue-400 mb-1">💡 Optimization Tips:</h5>
            <ul className="text-xs space-y-1">
              {atsInfo.optimization_tips.map((tip, idx) => (
                <li key={idx} className="text-gray-300">{tip}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  // Show loading while auth is initializing
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-white mb-2">Loading...</h2>
          <p className="text-gray-400">Initializing secure authentication</p>
        </div>
      </div>
    );
  }

  // Show professional loading state while connecting to authentication
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-white mb-2">Connecting to ATS Insider...</h2>
          <p className="text-gray-400">Establishing secure connection</p>

          {/* Show fallback options after a few seconds if still loading */}
          <div className="mt-8 space-y-3 max-w-md mx-auto">
            <button
              onClick={() => {
                window.location.href = 'https://atsinsiderai.com'
              }}
              className="w-full bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors text-sm"
            >
              Visit Main Site
            </button>
            <button
              onClick={() => window.location.reload()}
              className="w-full bg-gray-700 text-gray-300 px-6 py-2 rounded-lg font-medium hover:bg-gray-600 transition-colors text-sm"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <AuthWrapper>
      {/* Upgrade Prompt Modal */}
      {showUpgrade && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6 text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Ready to Unlock More Opportunities?</h2>
            <p className="text-gray-600 mb-4">
              You've used all {upgradeData?.usage?.used || 0} of your {upgradeData?.usage?.limit || 2} monthly searches.
              Upgrade now to get more searches and continue finding your perfect job matches.
            </p>
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="text-sm text-gray-600">
                <div className="flex justify-between items-center mb-2">
                  <span>Searches Used:</span>
                  <span className="font-semibold">{upgradeData?.usage?.used || 0}/{upgradeData?.usage?.limit || 2}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Current Plan:</span>
                  <span className="font-semibold capitalize">{upgradeData?.subscription_tier || 'Free'}</span>
                </div>
              </div>
            </div>
            <div className="space-y-3">
              <button
                onClick={redirectToUpgrade}
                className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-blue-600 hover:to-indigo-700 transition-all duration-300"
              >
                Upgrade Now
              </button>
              <button
                onClick={() => setShowUpgrade(false)}
                className="w-full border border-gray-300 text-gray-700 py-3 px-6 rounded-lg font-semibold hover:bg-gray-50 transition-all duration-300"
              >
                Maybe Later
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="min-h-screen transition-all duration-300 bg-gradient-to-br from-gray-900 via-gray-900 to-blue-900/20">
      {/* Navigation Bar */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-gray-900/80 border-gray-800 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="bg-gradient-to-br from-blue-500 to-indigo-600 w-10 h-10 rounded-xl flex items-center justify-center shadow-lg">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-500 rounded-full animate-pulse"></div>
              </div>
              <div className="flex-1">
                <h1 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                  ATS Insider
                </h1>
                <p className="text-xs text-gray-400">Beat the System • ATS Intelligence</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              {showResults && (
                <button 
                  onClick={resetApp}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-all hover:scale-105 bg-gray-800 hover:bg-gray-700 text-gray-300"
                >
                  New Search
                </button>
              )}
              <div className="flex items-center gap-2">
                <div className="px-3 py-1 rounded-lg bg-emerald-500/10 text-emerald-400 text-xs font-medium">
                  {companyCount} Companies
                </div>
                <div className="px-3 py-1 rounded-lg bg-blue-500/10 text-blue-400 text-xs font-medium">
                  {atsCount} ATS Systems
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!showResults ? (
          /* Upload Section */
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-4xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                Beat Every ATS System
              </h2>
              <p className="text-lg text-gray-300 max-w-xl mx-auto">
                Get insider knowledge about {companyCount}+ companies across {atsCount} major ATS platforms. Know exactly how to optimize your resume for each system.
              </p>
              <div className="flex flex-wrap justify-center gap-4 mt-6 text-sm">
                <span className="flex items-center gap-2 text-green-400">
                  🔍 Deep Company Analysis
                </span>
                <span className="flex items-center gap-2 text-blue-400">
                  🎯 Live Job Market Data
                </span>
                <span className="flex items-center gap-2 text-purple-400">
                  ⚙️ Platform-Specific Tips
                </span>
              </div>
            </div>

            <div className="relative p-8 rounded-3xl border backdrop-blur-sm border-gray-700/50 bg-gray-800/30 shadow-2xl">
              {/* File Upload Area */}
              <div className={`relative border-2 border-dashed p-8 rounded-2xl text-center cursor-pointer transition-all duration-300 group ${
                resume 
                  ? 'border-emerald-500 bg-emerald-500/5' 
                  : 'border-gray-600 bg-gray-800/20 hover:border-blue-500 hover:bg-blue-500/5'
              }`}>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={handleFileChange}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" 
                />
                <div className="pointer-events-none">
                  {resume ? (
                    <div className="space-y-3">
                      <div className="w-16 h-16 mx-auto bg-emerald-500/10 rounded-2xl flex items-center justify-center">
                        <svg className="w-8 h-8 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-emerald-400 font-semibold text-lg">{resume.name}</p>
                        <p className="text-sm text-gray-500">{(resume.size / 1024 / 1024).toFixed(2)} MB • {resume.name.toLowerCase().endsWith('.pdf') ? 'PDF' : 'Word'} Document</p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="w-16 h-16 mx-auto bg-blue-500/10 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                        <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-lg font-semibold mb-1">Upload Your Resume</p>
                        <p className="text-sm text-gray-500">Drag and drop or click to browse • PDF or Word • Max 10MB</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Controls */}
              <div className="mt-8 space-y-6">

                {/* Location Input */}
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-3">
                    📍 Job Location
                  </label>
                  <input 
                    type="text"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="e.g., San Francisco, CA or Remote"
                    className="w-full p-3 rounded-xl border text-sm font-medium transition-all bg-gray-800 border-gray-700 text-white focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 placeholder-gray-400"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    💡 Try "Remote", city names, or "San Francisco, CA" for best results
                  </p>
                </div>

                {/* Country Selection */}
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-3">
                    🌍 Country
                  </label>
                  <select 
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    className="w-full p-3 rounded-xl border text-sm font-medium transition-all bg-gray-800 border-gray-700 text-white focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  >
                    <option value="US">🇺🇸 United States</option>
                    <option value="UK">🇬🇧 United Kingdom</option>
                    <option value="CA">🇨🇦 Canada</option>
                    <option value="AU">🇦🇺 Australia</option>
                    <option value="DE">🇩🇪 Germany</option>
                    <option value="FR">🇫🇷 France</option>
                    <option value="NL">🇳🇱 Netherlands</option>
                    <option value="SG">🇸🇬 Singapore</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-2">
                    🌎 Jobs will be filtered to your selected country
                  </p>
                </div>

                <button 
                  onClick={handleUpload} 
                  disabled={!resume || loading} 
                  className={`w-full py-4 rounded-2xl font-bold text-lg transition-all duration-300 ${
                    resume && !loading 
                      ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:scale-105 hover:shadow-2xl shadow-lg' 
                      : 'bg-gray-300 dark:bg-gray-700 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {loading ? (
                    <div className="flex items-center justify-center gap-3">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Analyzing with AI...
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      Analyze Resume
                    </div>
                  )}
                </button>
              </div>

              {/* Progress Indicator */}
              {loading && (
                <div className="mt-6 space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-blue-400 animate-pulse">{currentStep}</p>
                    <span className="text-sm font-bold text-blue-400">{progress}%</span>
                  </div>
                  <div className="w-full bg-gray-700 dark:bg-gray-800 h-2 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 transition-all duration-300 ease-out" 
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                </div>
              )}

              {/* Error Display */}
              {error && (
                <div className="mt-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
                  <div className="flex items-center gap-3">
                    <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-red-400 font-medium">{error}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* Results Section */
          <div className="space-y-8">
            {/* Results Header */}
            <div className="text-center space-y-4">
              <h2 className="text-3xl font-bold bg-gradient-to-r from-emerald-600 to-blue-600 bg-clip-text text-transparent">
                Backend Analysis Complete
              </h2>
              <div className="flex items-center justify-center gap-8 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-emerald-500 rounded-full"></div>
                  <span className="text-gray-300">{matches.length} Real Job Matches</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                  <span className="text-gray-300">{aiTitles.length} AI-Generated Titles</span>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex justify-center">
              <div className={`inline-flex p-1 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                <button
                  onClick={() => setActiveTab('matches')}
                  className={`px-6 py-3 rounded-xl font-semibold transition-all ${
                    activeTab === 'matches'
                      ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg'
                      : 'text-gray-300 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  Job Matches ({filtersActive ? `${filteredMatches.length}/` : ''}{matches.length})
                </button>
                <button
                  onClick={() => setActiveTab('titles')}
                  className={`px-6 py-3 rounded-xl font-semibold transition-all ${
                    activeTab === 'titles'
                      ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg'
                      : 'text-gray-300 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  AI Suggestions ({aiTitles.length})
                </button>
              </div>
            </div>

            {/* Filter + Sort Bar */}
            {activeTab === 'matches' && matches.length > 0 && (
              <div className="rounded-2xl border border-gray-700/50 bg-gray-800/40 backdrop-blur-sm p-4 space-y-3">
                {/* Row 1: sort + result count */}
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400 uppercase tracking-wide font-semibold">Sort</span>
                    <div className="flex rounded-lg overflow-hidden border border-gray-700 text-sm">
                      {[['score', '🎯 Best Match'], ['recent', '🕐 Most Recent']].map(([val, label]) => (
                        <button
                          key={val}
                          onClick={() => setSortBy(val)}
                          className={`px-3 py-1.5 transition-colors ${sortBy === val ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-700'}`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-400">
                      {filtersActive
                        ? <><span className="text-white font-semibold">{filteredMatches.length}</span> of {matches.length} jobs</>
                        : <><span className="text-white font-semibold">{matches.length}</span> jobs</>
                      }
                    </span>
                    {filtersActive && (
                      <button
                        onClick={resetFilters}
                        className="text-xs px-2 py-1 rounded-md bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
                      >
                        ✕ Clear filters
                      </button>
                    )}
                  </div>
                </div>

                {/* Row 2: filter toggles */}
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-xs text-gray-400 uppercase tracking-wide font-semibold">Filter</span>

                  {/* Remote toggle */}
                  <button
                    onClick={() => setFilterRemote(v => !v)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                      filterRemote
                        ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-300'
                        : 'border-gray-700 text-gray-400 hover:border-gray-500 hover:text-gray-300'
                    }`}
                  >
                    🌍 Remote only
                  </button>

                  {/* Has salary toggle */}
                  <button
                    onClick={() => setFilterSalary(v => !v)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                      filterSalary
                        ? 'bg-green-500/20 border-green-500/50 text-green-300'
                        : 'border-gray-700 text-gray-400 hover:border-gray-500 hover:text-gray-300'
                    }`}
                  >
                    💰 Has salary
                  </button>

                  {/* Employment type dropdown — only shown if multiple types exist */}
                  {typeOptions.length > 1 && (
                    <select
                      value={filterType}
                      onChange={e => setFilterType(e.target.value)}
                      className={`px-3 py-1.5 rounded-lg text-sm border bg-gray-900 transition-colors cursor-pointer ${
                        filterType !== 'all'
                          ? 'border-blue-500/50 text-blue-300'
                          : 'border-gray-700 text-gray-400 hover:border-gray-500'
                      }`}
                    >
                      <option value="all">💼 All types</option>
                      {typeOptions.map(t => (
                        <option key={t} value={t}>
                          {t.charAt(0).toUpperCase() + t.slice(1)}
                        </option>
                      ))}
                    </select>
                  )}

                  {/* ATS dropdown — only shown if multiple ATS systems exist */}
                  {atsOptions.length > 1 && (
                    <select
                      value={filterATS}
                      onChange={e => setFilterATS(e.target.value)}
                      className={`px-3 py-1.5 rounded-lg text-sm border bg-gray-900 transition-colors cursor-pointer ${
                        filterATS !== 'all'
                          ? 'border-indigo-500/50 text-indigo-300'
                          : 'border-gray-700 text-gray-400 hover:border-gray-500'
                      }`}
                    >
                      <option value="all">🔧 All platforms</option>
                      {atsOptions.map(a => (
                        <option key={a} value={a}>
                          {a.charAt(0).toUpperCase() + a.slice(1)}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
            )}

            {/* Results Content */}
            {activeTab === 'matches' ? (
              <div className="space-y-4">
                {filteredMatches.length > 0 ? (
                  filteredMatches.map((match, index) => (
                    <div
                      key={index}
                      className={`p-6 rounded-2xl border backdrop-blur-sm hover:scale-[1.02] transition-all duration-300 cursor-pointer ${
                        darkMode ? 'border-gray-700/50 bg-gray-800/30 hover:bg-gray-800/50' : 'border-gray-200/50 bg-white/70 hover:bg-white/90'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 space-y-3">
                          <div className="flex items-start gap-3">
                            <div className="flex-1">
                              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                                {match.title}
                              </h3>
                              <p className="text-lg text-blue-600 dark:text-blue-400 font-semibold">
                                {match.company}
                              </p>
                              {match.location && match.location !== 'Location not specified' && (
                                <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                                  📍 {match.location}
                                </p>
                              )}
                            </div>
                            <div className="flex flex-col items-end gap-2">
                              <div className={`px-3 py-1 rounded-full text-sm font-bold ${getScoreBadgeColor(match.score)} text-white`}>
                                {match.score}% Match
                              </div>
                              <div className="flex gap-2 items-center">
                                {match.ats_info && (
                                  <span className={`text-xs px-2 py-1 rounded-full text-white font-medium ${getATSBadgeColor(match.source_platform)}`}>
                                    {match.ats_info.display_name || match.ats?.toUpperCase()}
                                  </span>
                                )}
                                {match.ats_info && (
                                  <button
                                    onClick={() => toggleATSTips(index)}
                                    className="text-xs px-2 py-1 rounded-full border transition-all hover:scale-105 border-gray-600 text-gray-400 hover:text-white hover:bg-gray-700"
                                  >
                                    {showATSTips[index] ? '🔽 Hide Tips' : '💡 ATS Tips'}
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          {/* Enhanced Metadata */}
                          <div className="flex flex-wrap gap-2 text-sm">
                            {/* ATS Type Badge */}
                            {match.ats_type && match.ats_type !== 'unknown' && (
                              <span className="px-2 py-1 bg-indigo-500/10 text-indigo-400 rounded-md font-medium">
                                🔧 {match.ats_type.charAt(0).toUpperCase() + match.ats_type.slice(1)}
                              </span>
                            )}
                            {/* Employment Type */}
                            {(match.employment_type || match.enhanced_metadata?.job_type) && (
                              <span className="px-2 py-1 bg-blue-500/10 text-blue-400 rounded-md">
                                💼 {match.employment_type || match.enhanced_metadata.job_type}
                              </span>
                            )}
                            {/* Salary Range */}
                            {(match.salary_range || match.enhanced_metadata?.salary_range) && (
                              <span className="px-2 py-1 bg-green-500/10 text-green-400 rounded-md">
                                💰 {match.salary_range || match.enhanced_metadata.salary_range}
                              </span>
                            )}
                            {/* Remote Status */}
                            {match.remote_ok && (
                              <span className="px-2 py-1 bg-cyan-500/10 text-cyan-400 rounded-md">
                                🌍 Remote
                              </span>
                            )}
                            {/* Posting Date */}
                            {(match.days_ago_text || match.enhanced_metadata?.posting_date || match.date_posted) && (
                              <span className="px-2 py-1 bg-purple-500/10 text-purple-400 rounded-md">
                                📅 {match.days_ago_text ||
                                    (match.enhanced_metadata?.posting_date ? new Date(match.enhanced_metadata.posting_date).toLocaleDateString() : null) ||
                                    (match.date_posted ? new Date(match.date_posted).toLocaleDateString() : 'Recently')}
                              </span>
                            )}
                            {/* Verification Status */}
                            {match.verification_status === 'api_verified' && (
                              <span className="px-2 py-1 bg-emerald-500/10 text-emerald-400 rounded-md">
                                ✓ Verified
                              </span>
                            )}
                          </div>

                          <p className="text-gray-300 line-clamp-3">
                            {(() => {
                              const desc = match.enhanced_metadata?.clean_description || match.description;
                              // Strip HTML tags if present
                              const stripped = desc.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
                              return stripped || desc;
                            })()}
                          </p>

                          <div className="flex items-center justify-end pt-2">
                            {match.link && match.link !== '#' && (
                              <a
                                href={match.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-medium rounded-lg hover:scale-105 transition-transform"
                                onClick={(e) => e.stopPropagation()}
                              >
                                Apply Now
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                              </a>
                            )}
                          </div>
                          
                          {/* ATS Optimization Tips Panel */}
                          {showATSTips[index] && renderATSTips(match.ats_info)}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    {filtersActive ? (
                      <>
                        <p className="text-lg font-medium">No jobs match these filters</p>
                        <button onClick={resetFilters} className="mt-3 text-sm text-blue-400 hover:text-blue-300 underline">
                          Clear all filters
                        </button>
                      </>
                    ) : (
                      <>
                        <p className="text-lg font-medium">No matches found</p>
                        <p className="text-sm">Try a different resume or location.</p>
                      </>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {aiTitles.length > 0 ? (
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {aiTitles.map((title, index) => (
                      <div
                        key={index}
                        className={`p-4 rounded-xl border backdrop-blur-sm transition-all duration-300 ${
                          darkMode ? 'border-gray-700/50 bg-gray-800/30' : 'border-gray-200/50 bg-white/70'
                        } ${title.feedback ? 'ring-2 ' + (title.feedback === 'relevant' ? 'ring-green-500' : 'ring-red-500') : ''}`}
                      >
                        <h3 className="font-bold text-gray-900 dark:text-white mb-2">
                          {title.title}
                        </h3>
                        <p className="text-sm text-gray-300 mb-3">
                          {title.description}
                        </p>
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-xs px-2 py-1 bg-blue-500/10 text-blue-600 dark:text-blue-400 rounded-full">
                            AI Recommended
                          </span>
                          <span className="text-xs text-gray-500">
                            {title.confidence}% confidence
                          </span>
                        </div>
                        <div className="flex items-center justify-between pt-3 border-t border-gray-700/30">
                          <span className="text-xs text-gray-400">Is this relevant?</span>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleTitleFeedback(title.title, 'relevant', index)}
                              disabled={title.feedback === 'relevant'}
                              className={`p-1.5 rounded transition-all ${
                                title.feedback === 'relevant'
                                  ? 'bg-green-500 text-white scale-110'
                                  : 'hover:bg-green-500/20 text-green-500'
                              }`}
                              title="Relevant"
                            >
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleTitleFeedback(title.title, 'not_relevant', index)}
                              disabled={title.feedback === 'not_relevant'}
                              className={`p-1.5 rounded transition-all ${
                                title.feedback === 'not_relevant'
                                  ? 'bg-red-500 text-white scale-110'
                                  : 'hover:bg-red-500/20 text-red-500'
                              }`}
                              title="Not Relevant"
                            >
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M18 9.5a1.5 1.5 0 11-3 0v-6a1.5 1.5 0 013 0v6zM14 9.667v-5.43a2 2 0 00-1.105-1.79l-.05-.025A4 4 0 0011.055 2H5.64a2 2 0 00-1.962 1.608l-1.2 6A2 2 0 004.44 12H8v4a2 2 0 002 2 1 1 0 001-1v-.667a4 4 0 01.8-2.4l1.4-1.866a4 4 0 00.8-2.4z" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                    <p className="text-lg font-medium">No AI suggestions from backend</p>
                    <p className="text-sm">Check if OpenAI integration is working in backend.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        
        {/* Footer */}
        <footer className="mt-16 pt-8 border-t border-gray-800">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-400">
              <p>© 2025 ATS Insider - Powered by AI</p>
              <p className="mt-1">Helping job seekers optimize for {companyCount} companies across {atsCount} ATS platforms</p>
            </div>
          </div>
        </footer>

        {/* Auth Debug Panel - for troubleshooting mobile authentication */}
        <AuthDebugPanel />
      </main>
    </div>
    </AuthWrapper>
  );
}

export default App;
